import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import { useFormikContext } from 'formik';
import { observer } from 'mobx-react';
import { Grid } from '@material-ui/core';
import { format } from 'date-fns';
import copy from 'copy-to-clipboard';
import classNames from 'classnames';
import { useHistory } from 'react-router-dom';
import { allowBindUDRStates, policyType as PolicyType } from '@ourbranch/lookups';
import { offerUsesAdvanceQuoteDays } from '../../helpers/advance-quote-helpers';
import { isPastOrWithin7Days, isEffectiveDateInPast } from '../../helpers/date-helpers';
import { useLazyQuery } from '@apollo/client';

import { useToast } from 'core/components/toast';
import { useStore } from 'core/store/store.mobx';
import useSession from 'core/hooks/use-session';
import { offerHasValidOptions } from 'core/helpers/underwriting-form-helpers';
import { getMVRCheckStatus } from 'core/helpers/mvr-helpers';
import { getPolicyType } from 'core/helpers/policy-type';
import FillSkippedDataPulls from './components/fill-skipped-data-pulls';
import FEDOfferFooter from './components/fed-offer-footer';
import OfferExpired from './components/offer-expired';
import UpdateOffer from './components/update-offer';
import AQDOfferFooter from './components/aqd-past-effective-date';
import UWForm from './components/UW-form';
import Checkout from './components/checkout';
import FarmOfferFooter from './components/farm-offer-footer';
import InvalidFooter from './components/invalid-footer';
import { RECALCULATE_QUOTE_TO_CLUSTER } from 'core/store/offer-store/offer-queries';
import { types } from 'core/helpers/sanitize';
import BranchPlusOnlyFooter from './components/branch-plus-only-footer';
import alertDarkGreen from 'core/assets/svg/alert-dark-green.svg';
import alertWhite from 'core/assets/svg/alert-white-bold.svg';
import checkmark from 'core/assets/svg/check.svg';
import useStyles from './footer.styles';

const Footer = observer(({ showValidationErrorsModal, setShowValidationErrorsModal, hasDisabledAffinity }) => {
  const classes = useStyles();
  const history = useHistory();
  const toast = useToast();
  const { handleSubmit, dirty, values, validateForm, setErrors, setTouched } = useFormikContext();
  const {
    offer: store,
    account: { sendEventAsMember }
  } = useStore();
  const session = useSession();
  const { isAgency } = session;

  const { cars, drivers, global } = store.offer.quote;
  const { setShowUDRModal, isStale, isOutdated, state } = store;
  const optionsAvailable = offerHasValidOptions({
    offer: store?.offer,
    priorQuoteWithPreBindUWRejections: store.priorQuoteWithPreBindUWRejections
  });
  const offerId = store?.offer?.id;
  const quoteDetails = types.QuoteDetailsInput(store?.offer?.quote);

  const { hasMVRToRun, hasPostBindMVrs } = getMVRCheckStatus(store, dirty, values);
  const needToUpdateMVRs = hasMVRToRun || hasPostBindMVrs;
  const hasInvalidVins =
    values?.selectedOption?.includes(PolicyType.Auto) && (cars || []).some((car) => String(car.make).startsWith('*'));
  const isExpiredOffer = isStale && isOutdated; // more than 30 days
  const isOutOfDateOffer = isStale && !isOutdated; // between 1-29 days old
  const needToFillSkippedData =
    !isOutOfDateOffer && !hasInvalidVins && !dirty && store.offer && store.offer.skippedDataPulls?.length > 0;
  const hasUDR = drivers.some((driver) => driver.autoViolations?.UDR >= 1) && !allowBindUDRStates.includes(store.state);

  // Farm underwriting approval details
  const approvalDetails = store.offer?.quote?.preBindUWCheck?.approvalDetails;
  const sentToUnderwritingAt = approvalDetails?.sentToUnderwritingAt;
  const sentToUnderwritingBy = approvalDetails?.sentToUnderwritingBy;
  const underwriterApprovedAt = approvalDetails?.underwriterApprovedAt;
  const underwriterApprovedBy = approvalDetails?.underwriterApprovedBy;
  const formattedSentDate = sentToUnderwritingAt
    ? format(new Date(sentToUnderwritingAt), "MM/dd/yyyy '@' h:mm a 'EST'")
    : null;
  const formattedApprovedDate = underwriterApprovedAt
    ? format(new Date(underwriterApprovedAt), "MM/dd/yyyy '@' h:mm a 'EST'")
    : null;
  const farmOfferWaitingForApproval =
    store.selectedOption === PolicyType.Farm && sentToUnderwritingAt && !underwriterApprovedAt;
  const farmOfferIsApproved = store.selectedOption === PolicyType.Farm && sentToUnderwritingAt && underwriterApprovedAt;

  const [recalculateQuoteToCluster, { loading }] = useLazyQuery(RECALCULATE_QUOTE_TO_CLUSTER, {
    variables: { offerId, revisedQuoteDetails: quoteDetails }
  });

  const onCheckout = useCallback(async () => {
    const checkoutErrors = await validateForm();
    if (Object.keys(checkoutErrors).length > 0) {
      setErrors(checkoutErrors);
      setTouched(checkoutErrors);
      setShowValidationErrorsModal(true);
      await sendEventAsMember({
        eventName: 'Checkout validation error - Applicant',
        userId: store?.offer?.webUserId,
        properties: JSON.stringify({
          isAgency,
          validationErrors: checkoutErrors
        })
      });
    } else if (hasUDR) {
      setShowUDRModal(true);
    } else {
      await sendEventAsMember({
        eventName: 'Staff checkout viewed - Applicant',
        userId: store?.offer?.webUserId,
        properties: JSON.stringify({
          isAgency,
          policyTypes: values.selectedOption
        })
      });
      history.push(`/offer/${store.offer.id}/${values.selectedOption}/checkout`);
    }
  }, [
    history,
    setErrors,
    setTouched,
    store.offer.id,
    store?.offer?.webUserId,
    values,
    isAgency,
    sendEventAsMember,
    setShowValidationErrorsModal,
    setShowUDRModal,
    validateForm,
    hasUDR
  ]);

  const onContinueToUWForm = useCallback(async () => {
    const updateErrors = await validateForm();
    if (Object.keys(updateErrors).length > 0) {
      setErrors(updateErrors);
      setTouched(updateErrors);
      setShowValidationErrorsModal(true);
    } else if (hasUDR) {
      setShowUDRModal(true);
    } else {
      history.push(`/offer/${store.offer.id}/verifyUW`);
    }
  }, [
    history,
    setShowValidationErrorsModal,
    setShowUDRModal,
    validateForm,
    setErrors,
    setTouched,
    store.offer.id,
    hasUDR
  ]);

  const onUpdate = async () => {
    if (!needToFillSkippedData) {
      const updateErrors = await validateForm();
      if (Object.keys(updateErrors).length > 0) {
        setErrors(updateErrors);
        setTouched(updateErrors);
        setShowValidationErrorsModal(true);
      } else {
        handleSubmit();
      }
    }
  };

  const copyLink = async (readOnly) => {
    const { protocol, host } = window.location;
    const newHost = host.replace(/staff\./i, 'www.'); // staff.ourbranch.com -> www.ourbranch.com, for example

    const selectedOption = values.selectedOption;

    let policyType;
    if (selectedOption === 'HB' || selectedOption === 'AB') {
      // if we're giving a bundled price, link to the bundle
      policyType = 'HA';
    } else {
      policyType = selectedOption;
    }

    if (!readOnly) {
      const link = store.offer.clusterId
        ? `${protocol}//${newHost}/review?cid=${store.offer.clusterId}&planType=${store.offer.code}&policyType=${policyType}&internal=true`
        : `${protocol}//${newHost}/quote/detail/${store.offer.id}`;
      const copied = copy(link);
      if (copied) {
        toast.notify({
          type: 'success',
          message: 'The link to a customizable quote has been copied to your clipboard.'
        });
      } else {
        toast.notify({
          type: 'error',
          message: `We could not copy the link, but here it is: ${link}`
        });
      }
    } else {
      const checkoutErrors = await validateForm();

      let clusterId;
      if (store.offer.clusterId) {
        clusterId = store.offer.clusterId;
      } else {
        const { data } = await recalculateQuoteToCluster();
        clusterId = data.cluster.id;
      }

      const readOnlyLink = `${protocol}//${newHost}/offer-review?cid=${clusterId}&policyType=${policyType}`;
      const copiedLink = copy(readOnlyLink);

      if (Object.keys(checkoutErrors).length > 0) {
        setErrors(checkoutErrors);
        setTouched(checkoutErrors);
        setShowValidationErrorsModal(true);
      } else if (hasUDR) {
        setShowUDRModal(true);
      } else if (copiedLink) {
        toast.notify({
          type: 'success',
          message: 'The link to a read-only quote has been copied to your clipboard.',
          durationInSeconds: 5
        });
      } else {
        toast.notify({
          type: 'error',
          message: `We could not copy the link, but here it is: ${readOnlyLink}`
        });
      }
    }
  };

  const { hasHome, hasCondo, isRenters, isFarm, hasAuto } = getPolicyType(store.selectedOption);
  const { homeEffectiveDate, autoEffectiveDate, condoEffectiveDate, rentersEffectiveDate, farmEffectiveDate } =
    (values && values.global) || global;
  let dwellingEffectiveDate;
  let dwellingPolicyType;
  if (hasHome) {
    dwellingEffectiveDate = homeEffectiveDate;
    dwellingPolicyType = PolicyType.Home;
  } else if (hasCondo) {
    dwellingEffectiveDate = condoEffectiveDate;
    dwellingPolicyType = PolicyType.Condo;
  } else if (isRenters) {
    dwellingEffectiveDate = rentersEffectiveDate;
    dwellingPolicyType = PolicyType.Renters;
  } else if (isFarm) {
    dwellingEffectiveDate = farmEffectiveDate;
    dwellingPolicyType = PolicyType.Farm;
  }

  const dwellingNeedsUpdate = dwellingEffectiveDate && isPastOrWithin7Days(dwellingEffectiveDate, state);
  const autoNeedsUpdate = autoEffectiveDate && isPastOrWithin7Days(autoEffectiveDate, state);
  const hasAdvanceQuotePolicy = offerUsesAdvanceQuoteDays(state, store.selectedOption);

  const dwellingEffectiveDateInPast = dwellingEffectiveDate && isEffectiveDateInPast(dwellingEffectiveDate, state);
  const autoEffectiveDateInPast = autoEffectiveDate && isEffectiveDateInPast(autoEffectiveDate, state);
  const hasEffectiveDateInPast = dwellingEffectiveDateInPast || autoEffectiveDateInPast;

  //need to check if effective dates have been changed
  const effectiveDatesDirty =
    (!!hasHome &&
      !!values?.global?.homeEffectiveDate &&
      values.global.homeEffectiveDate !== global.homeEffectiveDate) ||
    (!!hasAuto &&
      !!values?.global?.autoEffectiveDate &&
      values.global.autoEffectiveDate !== global.autoEffectiveDate) ||
    (!!hasCondo &&
      !!values?.global?.condoEffectiveDate &&
      values.global.condoEffectiveDate !== global.condoEffectiveDate) ||
    (!!isRenters &&
      !!values?.global?.rentersEffectiveDate &&
      values.global.rentersEffectiveDate !== global.rentersEffectiveDate);

  const shouldShowAQDFooter = hasAdvanceQuotePolicy && (hasEffectiveDateInPast || effectiveDatesDirty);

  const [shouldShowCheckboxAnUpdateButton, setShouldShowCheckboxAnUpdateButton] = useState(
    dwellingNeedsUpdate || autoNeedsUpdate
  );
  const invalidOffer = hasDisabledAffinity || !optionsAvailable;

  return (
    <div id="offer_footer" className={classes.floatingContainer}>
      <div
        className={classNames(
          classes.footerContainer,
          {
            [classes.footerAlert]: isExpiredOffer || !optionsAvailable || hasDisabledAffinity
          },
          {
            [classes.extendedFooterContainer]:
              !invalidOffer &&
              isOutOfDateOffer &&
              shouldShowCheckboxAnUpdateButton &&
              !hasAdvanceQuotePolicy &&
              !(farmOfferIsApproved || farmOfferWaitingForApproval)
          },
          {
            [classes.footerContainerWithBanner]: farmOfferWaitingForApproval || farmOfferIsApproved
          }
        )}
      >
        <Grid container alignItems="center" className={classes.footerContent}>
          {hasDisabledAffinity ? (
            <InvalidFooter text="Sorry, you can't update the offer because your affinity code is disabled." />
          ) : (
            (store.isBranchPlusOnly && <BranchPlusOnlyFooter />) ||
            (isOutOfDateOffer && !hasAdvanceQuotePolicy && (
              <FEDOfferFooter
                onUpdate={onUpdate}
                dwellingEffectiveDate={dwellingEffectiveDate}
                autoEffectiveDate={autoEffectiveDate}
                dwellingPolicyType={dwellingPolicyType}
                dwellingNeedsUpdate={dwellingNeedsUpdate}
                autoNeedsUpdate={autoNeedsUpdate}
                shouldShowCheckboxAnUpdateButton={shouldShowCheckboxAnUpdateButton}
                setShouldShowCheckboxAnUpdateButton={setShouldShowCheckboxAnUpdateButton}
              />
            )) ||
            (isExpiredOffer && <OfferExpired />) ||
            (!optionsAvailable && <InvalidFooter text="There are no valid policy options for this offer." />) ||
            // Show AQD footer when effective date includes today or in the past (on or after the effective date)
            (shouldShowAQDFooter && <AQDOfferFooter onUpdate={onUpdate} />) ||
            ((dirty || isOutOfDateOffer) && <UpdateOffer onUpdate={onUpdate} />) ||
            (store.selectedOption === PolicyType.Farm && (
              <FarmOfferFooter
                onContinueToCheckout={onCheckout}
                setShowValidationErrorsModal={setShowValidationErrorsModal}
              />
            )) ||
            (store.needsPreBindUWForm && (
              <UWForm showValidationErrorsModal={showValidationErrorsModal} onContinueToUWForm={onContinueToUWForm} />
            )) ||
            ((needToFillSkippedData || needToUpdateMVRs) && (
              <FillSkippedDataPulls copyLink={copyLink} setShowValidationErrorsModal={setShowValidationErrorsModal} />
            )) ||
            (!dirty && (
              <Checkout
                showValidationErrorsModal={showValidationErrorsModal}
                hasInvalidVins={hasInvalidVins}
                copyLink={copyLink}
                onCheckout={onCheckout}
                loading={loading}
              />
            ))
          )}
        </Grid>
        {farmOfferWaitingForApproval && (
          <div className={classes.sentToUnderwritingBanner}>
            <img src={alertDarkGreen} alt="" className={classes.bannerAlertIcon} />
            <span className={classes.sentToUnderwritingText}>
              This offer has been sent to underwriting on {formattedSentDate} by {sentToUnderwritingBy}.
              {!session.canApproveUWReview && ' You are not able to edit until underwriting has reviewed it.'}
            </span>
          </div>
        )}
        {farmOfferIsApproved && !dirty && (
          <div className={classes.approvedBanner}>
            <img src={checkmark} alt="" className={classes.bannerAlertIcon} />
            <span className={classes.sentToUnderwritingText}>
              This was approved for checkout by underwriting on {formattedApprovedDate} by {underwriterApprovedBy}. If
              you make changes to this offer, it may require another underwriting review.
            </span>
          </div>
        )}
        {farmOfferIsApproved && dirty && (
          <div className={classes.approvedDangerBanner}>
            <img src={alertWhite} alt="" className={classes.bannerAlertIcon} />
            <span className={classes.approvedDangerBannerText}>
              Changes made after approval. Once you're done updating, you may need to revisit the review page and
              resubmit to underwriting.
            </span>
          </div>
        )}
      </div>
    </div>
  );
});

Footer.propTypes = {
  showValidationErrorsModal: PropTypes.bool.isRequired,
  setShowValidationErrorsModal: PropTypes.func.isRequired
};

export default Footer;
